import clsx from 'clsx';
import {useEffect, useState} from 'react';

import {usdFormatter} from '@/lib/helpers';

import {useAuth} from '@/providers/components/authProvider';
import {useMarkets} from '@/providers/components/marketsProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {useExchangeOrder} from '../providers/exchangeOrder';

function SellOrderCard({
  onOpenProductSelection,
  onOpenMarketSearch,
  selectedMarket,
  onPositionFilled,
}: {
  onOpenProductSelection: (marketId: number | undefined) => void;
  onOpenMarketSearch: () => void;
  selectedMarket: {market_id: number; market_name: string} | null;
  onPositionFilled?: () => void;
}) {
  // Hooks
  const {getUserAccountBalance} = useAuth();
  const {getDistributorIdsForMarket} = useMarkets();
  const {sellOrderProduct, submitSellOrder, resetForms} = useExchangeOrder();
  const {toast} = useNotifications();

  // State
  const [limitPrice, setLimitPrice] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [orderType, setOrderType] = useState<'limit' | 'market'>('limit');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedMarket) {
      // const getDistributorIds = async () => {
      //   const distributorIds = await getDistributorIdsForMarket(selectedMarket.market_id);
      // };
      // getDistributorIds();
    }
  }, [selectedMarket, getDistributorIdsForMarket]);

  // Refs

  // ************************************************** */
  // ******************* QUERYS ******************* */
  // ************************************************** */

  /************************************************** */
  /****************** FUNCTIONALITY ******************* */
  /************************************************** */
  const resetForm = () => {
    setLimitPrice(0);
    setQuantity(0);
    setOrderType('limit');
  };

  const handleSubmitSellOrder = async () => {
    setIsLoading(true);
    try {
      const result = await submitSellOrder({
        quantity,
        limitPrice,
        orderType,
        marketId: selectedMarket?.market_id || 0,
      });

      if (result.success) {
        onPositionFilled?.();

        toast.success('Sell order submitted successfully');
        await getUserAccountBalance();
        resetForm();
        resetForms();
      } else {
        if (result.error?.toLowerCase().includes('insufficient')) {
          toast.error('Insufficient balance to complete this order');
        } else {
          toast.error(result.error || 'Failed to submit sell order');
        }
      }
    } catch (error) {
      console.error('Error submitting sell order', error);
      toast.error('Failed to submit sell order');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="box custom-box z-40">
        <div className="box-header">
          <div className="box-title">Sell Order</div>
        </div>
        <div className="box-body">
          <div>
            <div className="flex flex-col gap-2">
              <div className="flex w-full gap-2">
                <div
                  onClick={onOpenMarketSearch}
                  data-hs-overlay="#hs-market-modal"
                  className={clsx(
                    'flex w-full cursor-pointer items-center gap-2 rounded-sm border p-2',
                    'hover:bg-gray-50 dark:hover:bg-gray-800'
                  )}
                >
                  {selectedMarket ? (
                    <div className="flex items-center gap-2">
                      <span>{selectedMarket.market_name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500">
                      <i className="ri-search-line"></i>
                      <span>Search Markets...</span>
                    </div>
                  )}
                </div>
              </div>

              {!sellOrderProduct ? (
                <button
                  type="button"
                  className="ti-btn ti-btn-disabled-full btn-wave ti-btn-md mt-2 dark:border-defaultborder/10"
                  disabled={!selectedMarket}
                  data-hs-overlay="#hs-extralarge-modal"
                  onClick={() => onOpenProductSelection(selectedMarket?.market_id)}
                >
                  <i className="ri-shopping-cart-line me-2"></i>
                  Select Product
                </button>
              ) : (
                <div className="my-2 flex items-center justify-between">
                  <div className="flex w-40 items-center">
                    <img
                      src={sellOrderProduct.product_logo || sellOrderProduct.brand_logo || '/images/logo/logo.png'}
                      alt=""
                      className="avatar avatar-md !mb-0 me-2 rounded-md leading-none"
                    />
                    <div>
                      <p className="mb-0 font-semibold">
                        <p title="nft_name">{sellOrderProduct.product_name}</p>
                      </p>
                      <p className="text-[0.75rem] font-normal text-[#8c9097] dark:text-white/50" title="creator_name">
                        {sellOrderProduct.brand_name}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ti-btn ti-btn-outline-primary btn-wave ti-btn-sm"
                    data-hs-overlay="#hs-extralarge-modal"
                    onClick={() => onOpenProductSelection(selectedMarket?.market_id || 0)}
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                </div>
              )}
            </div>
            {sellOrderProduct && (
              <div>
                <div className="flex justify-between py-2 text-[.875rem]">
                  <div>
                    <span className="font-semibold text-dark">Price Low:</span>
                    <span
                      onClick={() => setLimitPrice(sellOrderProduct?.min_price || 0)}
                      className="ms-2 inline-block text-[.875rem] text-success"
                    >
                      {sellOrderProduct?.min_price ? `$${sellOrderProduct?.min_price.toFixed(2)}` : 'N/A'}
                    </span>
                    <span className="ms-2 font-semibold text-dark">USD</span>
                  </div>
                  <div>
                    <span className="font-semibold text-dark">Price High:</span>
                    <span
                      onClick={() => setLimitPrice(sellOrderProduct?.max_price || 0)}
                      className="ms-2 inline-block text-[#8c9097] text-danger"
                    >
                      {sellOrderProduct?.max_price ? `$${sellOrderProduct?.max_price.toFixed(2)}` : 'N/A'}
                    </span>
                    <span className="ms-2 font-semibold text-dark">USD</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-[.875rem] font-semibold">
                    Total Quantity:{' '}
                    <span
                      className="inline-block text-[.875rem]"
                      onClick={() => setQuantity(sellOrderProduct?.item_count || 0)}
                    >
                      {sellOrderProduct?.item_count} x{' '}
                      <span className="font-normal text-[#8c9097] dark:text-white/50">
                        {sellOrderProduct?.product_name}
                      </span>
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 rounded-md border border-gray-200 p-2 text-[.75rem]">
                    <div>
                      <div className="font-semibold">Pending</div>
                      <div className="cursor-pointer" onClick={() => setQuantity(sellOrderProduct?.item_count || 0)}>
                        {sellOrderProduct?.pending_quantity || 0}
                      </div>
                    </div>

                    <div>
                      <div className="font-semibold">Distributor</div>
                      <div className="cursor-pointer" onClick={() => setQuantity(sellOrderProduct?.item_count || 0)}>
                        {sellOrderProduct?.distributor_quantity || 0}
                      </div>
                    </div>

                    <div>
                      <div className="font-semibold">Retail</div>
                      <div className="cursor-pointer" onClick={() => setQuantity(sellOrderProduct?.item_count || 0)}>
                        {sellOrderProduct?.retail_quantity || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/*  LIMIT AND QUANTITY INPUTS */}
            {sellOrderProduct && (
              <div className="flex flex-col gap-4">
                {/* ORDER TYPE */}
                <div className="input-group crypto-buy-sell mt-3 flex-1 flex-col">
                  <label className="mb-2 text-[0.75rem] font-semibold">Order Type</label>
                  <div className="flex gap-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="limit"
                        name="orderType"
                        value="limit"
                        checked={orderType === 'limit'}
                        onChange={e => setOrderType(e.target.value as 'limit' | 'market')}
                        className="h-4 w-4 border-gray-300 bg-gray-100 text-primary focus:ring-2 focus:ring-primary"
                      />
                      <label htmlFor="limit" className="ml-2 text-sm font-medium">
                        Limit Order
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="market"
                        name="orderType"
                        value="market"
                        checked={orderType === 'market'}
                        onChange={e => setOrderType(e.target.value as 'limit' | 'market')}
                        className="h-4 w-4 border-gray-300 bg-gray-100 text-primary focus:ring-2 focus:ring-primary"
                      />
                      <label htmlFor="market" className="ml-2 text-sm font-medium">
                        Market Order
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  {/* PRICE LIMIT */}
                  <div className="crypto-buy-sell flex-1">
                    <label className="mb-2 flex items-center gap-1 text-[0.75rem] font-semibold">
                      {orderType === 'limit' ? 'Limit Price (USD)' : 'Market Price (USD)'}
                      <i
                        className="ri-information-line cursor-help"
                        title="Set the minimum price you want to sell your items for. Market orders will sell at the current best available price."
                      ></i>
                    </label>
                    {orderType === 'limit' ? (
                      <div className="relative flex w-full items-stretch justify-center gap-1 !rounded-md border border-defaultborder/10 p-1">
                        <button
                          type="button"
                          className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                          onClick={() => setLimitPrice(limitPrice > 0 ? limitPrice - 1 : limitPrice)}
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <input
                          type="number"
                          className="form-control border-none px-1 py-0 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          placeholder="Limit Price (USD)"
                          value={limitPrice || ''}
                          min={0}
                          max={sellOrderProduct?.max_factored_price}
                          onChange={e => {
                            const newValue = Number(e.target.value);
                            setLimitPrice(Math.min(newValue, sellOrderProduct?.max_factored_price || 0));
                          }}
                        />
                        <button
                          type="button"
                          className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                          onClick={() =>
                            setLimitPrice(
                              limitPrice < (sellOrderProduct?.max_factored_price || 0) ? limitPrice + 1 : limitPrice
                            )
                          }
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="pt-1 text-center text-lg font-semibold text-primary">
                        {usdFormatter.format(sellOrderProduct?.max_factored_price || 0)}
                      </div>
                    )}
                  </div>

                  {/* QUANTITY */}
                  <div className="crypto-buy-sell flex-1">
                    <label className="mb-2 flex items-center gap-1 text-[0.75rem] font-semibold">
                      Quantity
                      <i className="ri-information-line cursor-help" title="The number of items you want to sell"></i>
                    </label>
                    <div className="relative flex w-full items-stretch justify-center gap-1 !rounded-md border border-defaultborder/10 p-1">
                      <button
                        type="button"
                        className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                        onClick={() => setQuantity(quantity > 0 ? quantity - 1 : quantity)}
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        type="number"
                        className="form-control border-none px-1 py-0 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        placeholder="Quantity"
                        value={quantity}
                        min={0}
                        onChange={e => {
                          const newValue = Number(e.target.value);
                          setQuantity(Math.min(newValue, sellOrderProduct?.item_count || 0));
                        }}
                      />
                      <button
                        type="button"
                        className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                        onClick={() =>
                          setQuantity(quantity < (sellOrderProduct?.item_count || 0) ? quantity + 1 : quantity)
                        }
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 grid">
              <button
                type="button"
                className="ti-btn ti-btn-danger-full btn-wave ti-btn-lg dark:border-defaultborder/10"
                onClick={handleSubmitSellOrder}
                disabled={!selectedMarket || !quantity || (orderType === 'limit' && !limitPrice) || isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </span>
                ) : (
                  'SELL'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SellOrderCard;
